import React, { useState, useContext, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { selectLocalPeer, useHMSActions, useHMSStore } from "@100mslive/react-sdk";

import RemoteLayout from "./layout";
import { Conference } from "../../components/videoCalling";
import { WorkshopContext } from "../../contexts/workshop.context";
import { CountdownTimer, SymbolPicker, TaskGroupsStatus } from "../../components/workshop";
import { defaultRoles } from "../../util/workshop.util";
import { getNextRoute, getPreviousRoute } from "../../util/general.util";
import { toast } from "react-toastify";

const TASK_ID = 0;

const Task2 = () => {
  const hmsActions = useHMSActions();
  const currentUser = useHMSStore(selectLocalPeer);

  const [readyByModerator, setReadyByModerator] = useState(false);
  const [allGroups, setAllGroups] = useState([]);
  const [callGroups, setCallGroups] = useState({});
  const [groupTaskStatus, setGroupTaskStatus] = useState({});
  const [currentGroupRole, setCurrentGroupRole] = useState(undefined);
  const [groupId, setGroupId] = useState(undefined);
  const [userGroup, setUserGroup] = useState([]);
  const [startTask, setStartTask] = useState(false);
  const [finishedTask, setFinishedTask] = useState(false);
  const [alerted, setAlerted] = useState(false);
  const getReadyGroups = () => {
    if (!groupTaskStatus || typeof groupTaskStatus !== "object") {
      return 0;
    }
    return Object.entries(groupTaskStatus).filter(([key, status]) => {
      if (key === "undefined" || !status || Object.keys(status).length === 0) {
        return false;
      }
      return status.started === true && status.finished === true;
    }).length;
  };

  const allGroupsReady = allGroups.length === getReadyGroups();

  const {
    socket,
    user: { id, role } = {},
    workshopDetails: { platform } = {},
    connectedUsers,
    disconnectedUsers
  } = useContext(WorkshopContext);

  const { t } = useTranslation();

  const fetchUserGroups = useCallback(() => {
    // Fetch latest user groups whenever connected/disconnected users change
    if (!socket || !id) return;

    socket.emit(
      "userGroupForTask",
      { taskId: TASK_ID, userId: id },
      ({ groupIndex, userGroup, callGroups, currentGroupRole, groupTaskStatus }) => {
        setCurrentGroupRole(currentGroupRole);
        if (groupIndex !== undefined) setGroupId(groupIndex);
        if (userGroup) setUserGroup(userGroup);
        if (callGroups) setCallGroups(callGroups);
        if (groupTaskStatus) {
          setGroupTaskStatus(groupTaskStatus);
          if (groupTaskStatus?.[groupIndex]?.started) setStartTask(true);
          if (groupTaskStatus?.[groupIndex]?.finished) setFinishedTask(true);
        }
      }
    );

    socket.emit("allGroupsOfTask", TASK_ID, (groups) => {
      if (groups) setAllGroups(groups);
    });

    socket.emit("getGroupsWithCallRooms", TASK_ID, (callGroups) => {
      if (callGroups) setCallGroups(callGroups);
    });
  }, [socket, id]);

  useEffect(() => {
    if (groupId === undefined) {
      fetchUserGroups();
    } else {
      // Still fetch allGroups and callGroups on mount/update
      socket.emit("allGroupsOfTask", TASK_ID, (groups) => {
        if (groups) setAllGroups(groups);
      });

      socket.emit("getGroupsWithCallRooms", TASK_ID, (callGroups) => {
        if (callGroups) setCallGroups(callGroups);
      });
    }

    socket.emit("groupTaskStatus", TASK_ID, (status) => {
      if (status) setGroupTaskStatus(status);

      if (groupId === undefined || groupId < -1) return;

      if (status?.[groupId]?.started) setStartTask(true);

      if (status?.[groupId]?.finished) setFinishedTask(true);
    });

    socket.on("groupTaskStatus", (status) => {
      setGroupTaskStatus(status?.[TASK_ID]);

      if (groupId === undefined || groupId < 0) return;

      if (status?.[TASK_ID]?.[groupId]?.started) {
        setStartTask(true);
      }

      // if (status?.[TASK_ID]?.[groupId]?.ready) {
      //   setReadyByModerator(true);
      //   setCurrentGroupRole(callGroups?.[groupId]);
      // }

      if (status?.[TASK_ID]?.[groupId]?.finished) {
        setFinishedTask(true);
        setCurrentGroupRole(role);
      } else {
        setFinishedTask(false);
      }
    });

    socket.emit("getTaskActivityStatus", TASK_ID, (myTaskStatus) => {
      console.log("myTaskStatus", myTaskStatus);
      if (!myTaskStatus) return;
      setReadyByModerator(myTaskStatus?.ready);
    });

    socket?.on("taskActivityStatus", (taskActivityStatus) => {
      console.log("taskActivityStatus", taskActivityStatus);
      const myTaskStatus = taskActivityStatus[TASK_ID];
      if (!myTaskStatus) return;
      if (myTaskStatus?.ready) {
        setCurrentGroupRole(callGroups?.[groupId]);
        setReadyByModerator(true);
      }
    });

    // Cleanup
    return () => {
      socket.off("groupTaskStatus");
    };
  }, [id, role, groupId, socket]);

  useEffect(() => {
    if (!currentGroupRole || !currentUser) return;

    console.log("currentUser", currentUser.roleName);
    console.log("currentGroupRole", currentGroupRole);
    if (currentUser?.roleName?.toLowerCase() === currentGroupRole?.toLowerCase()) return;
    hmsActions.changeRoleOfPeer(currentUser.id, currentGroupRole?.toLowerCase(), true);
  }, [currentGroupRole, currentUser]);

  // Refetch user groups whenever connectedUsers or disconnectedUsers changes
  useEffect(() => {
    fetchUserGroups();
  }, [connectedUsers, disconnectedUsers, fetchUserGroups]);

  const handleNext = () => {
    if (!readyByModerator) {
      socket.emit("taskActivityStatus", {
        taskId: TASK_ID,
        status: { ready: true }
      });
    } else {
      if (!allGroupsReady && !alerted) {
        toast.warning(t("task2.notAllGroupsReady"), {
          closeButton: true
        });
        setAlerted(true);
        return;
      }
      const nextRoute = getNextRoute("/task-2", platform);
      socket.emit("navigate", nextRoute);
    }
  };

  const handleComplete = () => {
    updateTaskInfo({
      taskId: TASK_ID,
      groupId,
      started: true,
      finished: true
    });
    setFinishedTask(true);
  };

  const updateTaskInfo = (data) => {
    socket.emit("updateGroupTaskStatus", data);
  };

  const startActivity = () => {
    updateTaskInfo({
      taskId: TASK_ID,
      groupId,
      started: true,
      finished: false,
      ready: true
    });
  };

  const buttonText = !readyByModerator
    ? t("task2.createBreakoutRoom") + " →"
    : role !== defaultRoles.moderator && readyByModerator && !startTask
      ? t("task2.buttonStart")
      : role !== defaultRoles.moderator && !finishedTask && startTask
        ? t("task2.buttonComplete") + " →"
        : finishedTask
          ? t("task2.buttonNext") + " →"
          : startTask
            ? t("task2.buttonComplete") + " →"
            : t("task2.buttonNext") + " →";

  const handleButtonClick = () => {
    if (
      (role === defaultRoles.moderator || role === defaultRoles.moderatorAndParticipant) &&
      !readyByModerator
    ) {
      return handleNext;
    } else if (role !== defaultRoles.moderator && readyByModerator && !startTask) {
      return startActivity;
    } else if (role !== defaultRoles.moderator && !finishedTask && startTask) {
      return handleComplete;
    } else if (
      (role !== defaultRoles.participant && finishedTask) ||
      role === defaultRoles.moderator
    ) {
      return handleNext;
    } else {
      return null;
    }
  };

  const buttonColor = !readyByModerator
    ? "green"
    : role !== defaultRoles.moderator && readyByModerator && !startTask
      ? "orange"
      : role !== defaultRoles.moderator && !finishedTask && startTask
        ? "orange"
        : finishedTask
          ? "green"
          : startTask
            ? "orange"
            : "green";

  const handleBack = () => {
    const previousRoute = getPreviousRoute(`/task-2`, platform);
    socket.emit("navigate", previousRoute);
  };

  const showBackButton =
    role !== defaultRoles.participant && (!readyByModerator || (startTask && finishedTask));

  return (
    <RemoteLayout
      buttonText={buttonText}
      onButtonClick={handleButtonClick()}
      buttonColor={buttonColor}
      showParticipants={true}
      onBackClick={showBackButton && handleBack}>
      <div className="text-center mb-8">
        <h1 className="text-white text-2xl md:text-4xl flex items-center justify-center gap-2 text-white-100">
          <span className="text-yellow-500">🏆</span>{" "}
          {(role === defaultRoles.moderator && readyByModerator) ||
          (role !== defaultRoles.moderator && finishedTask)
            ? t("task2.waitingRoom.title")
            : role !== defaultRoles.moderator && readyByModerator
              ? t(startTask ? "task2.breakoutRoom.titleStarted" : "task2.breakoutRoom.title")
              : t("task2.title")}
        </h1>
        <p
          className="mt-4 max-w-2xl text-lg mx-auto text-white-100"
          dangerouslySetInnerHTML={{
            __html:
              (role === defaultRoles.moderator && readyByModerator) ||
              (role !== defaultRoles.moderator && finishedTask)
                ? t("task2.waitingRoom.description")
                : role !== defaultRoles.moderator && readyByModerator
                  ? t(
                      startTask
                        ? "task2.breakoutRoom.descriptionStarted"
                        : "task2.breakoutRoom.description"
                    )
                  : t("task2.description")
          }}
        />
      </div>
      {/* Main Content */}
      <div
        className={`flex flex-col gap-8 justify-center items-center max-w-6xl mx-auto ${
          startTask && !finishedTask ? "md:flex-col" : "md:flex-row"
        } ${role !== defaultRoles.moderator && readyByModerator && !finishedTask && "md:!flex-col !justify-center"}`}>
        {/* Task Overview Card */}
        <div className="flex flex-col gap-4 w-full">
          {!readyByModerator && !finishedTask && !startTask && (
            <div className="bg-orange-50 rounded-lg p-6 max-w-md">
              <h2
                className="text-orange-500 font-bold text-xl mb-4"
                dangerouslySetInnerHTML={{ __html: t("task2.listOfGroups.heading") }}
              />
              <p
                className="font-bold my-2"
                dangerouslySetInnerHTML={{ __html: t("task2.listOfGroups.subHeading") }}
              />
              <ul
                className="list-inside"
                dangerouslySetInnerHTML={{ __html: t("task2.listOfGroups.group1Description") }}
              />
            </div>
          )}

          {role !== defaultRoles.moderator && startTask && !finishedTask && (
            <div className="bg-orange-50 mx-auto rounded-full block md:hidden">
              <CountdownTimer
                id={`${TASK_ID}-${groupId}`}
                start={startTask}
                finished={finishedTask}
                trailColor="#fff"
                textColor="#E59536"
                strokeWidth={8}
                orangeClock={true}
                size={130}
                totalTime={90}
                onComplete={() =>
                  updateTaskInfo({
                    taskId: TASK_ID,
                    groupId,
                    started: true,
                    finished: true
                  })
                }
              />
            </div>
          )}

          {((role === defaultRoles.moderator && readyByModerator) ||
            (role !== defaultRoles.moderator && finishedTask)) && (
            <div className="flex flex-col gap-5">
              <TaskGroupsStatus
                isOnline={true}
                groups={allGroups}
                groupTaskStatus={groupTaskStatus}
                socket={socket}
                TASK_ID={TASK_ID}
                index={groupId}
                totalTime={90}
                t={t}
              />
            </div>
          )}
          {role !== defaultRoles.moderator && startTask && !finishedTask && (
            <div className="rounded-lg p-6 w-full">
              <SymbolPicker
                socket={socket}
                id={"task2"}
                updateId={"updateTask2"}
                groupId={groupId}
                popupHeading={t("mtActivityTwoPopupHeading", { returnObjects: true })}
                chosenOptionHeadings={t("mtActivityTwo", { returnObjects: true })}
                optionsHeadings={t("activityTwoOptions", { returnObjects: true })}
                optionsImages={[...Array(4).keys()].map((index) =>
                  require(`../../assets/images/task2/${index}.png`)
                )}
                finishedTask={finishedTask}
                addIconColor={"#E59536"}
              />
            </div>
          )}
        </div>

        <div
          className={`flex flex-row gap-8 justify-center md:justify-between items-center container mx-auto w-full ${role !== defaultRoles.moderator && readyByModerator && !finishedTask && "md:!justify-center"}`}>
          {role !== defaultRoles.moderator && startTask && !finishedTask && (
            <div className="bg-orange-50 rounded-full hidden md:block">
              <CountdownTimer
                id={`${TASK_ID}-${groupId}`}
                start={startTask}
                finished={finishedTask}
                trailColor="#fff"
                textColor="#E59536"
                strokeWidth={8}
                orangeClock={true}
                size={"90"}
                totalTime={90}
                onComplete={() =>
                  updateTaskInfo({
                    taskId: TASK_ID,
                    groupId,
                    started: true,
                    finished: true
                  })
                }
              />
            </div>
          )}
          {/* Participants Grid */}
          <Conference smallSpace={true} />
        </div>
      </div>
    </RemoteLayout>
  );
};

export default Task2;
